import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DSIcon } from '../design-system/primitives';
import { getCategoryLabel, isEventCancelled } from '../utils/eventHelpers';
import { monogram, eventImage } from './EventsBrowse';
import EventCancelledBanner from './EventCancelledBanner';
import './EventDetailMember.css';
import { personName } from '../utils/personName';
import UserAvatar from './UserAvatar';

const CHOICES = [
  { value: 'going', label: 'Going' },
  { value: 'interested', label: 'Interested' },
  { value: 'not_going', label: "Can't go" }
];

const PROMPT = {
  going: 'You are going',
  interested: 'You are interested',
  not_going: 'You are not going'
};

const fullName = personName;

const longDate = (value) => new Date(value).toLocaleDateString('en-US', {
  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
});

const clockTime = (value) => new Date(value).toLocaleTimeString('en-US', {
  hour: 'numeric', minute: '2-digit'
});

/** Trims to a word boundary rather than mid-word, the way the design does. */
const clamp = (text, length) => text.slice(0, length).replace(/\s+\S*$/, '') + '...';

/**
 * A host's bio, folded to four lines (the clamp is in the CSS). Read more only appears when
 * the text actually runs past them, which is measured, since a character count says nothing
 * about how many lines a bio wraps to at this width.
 */
function HostBio({ bio }) {
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element && !open) setOverflows(element.scrollHeight > element.clientHeight + 1);
  }, [bio, open]);

  return (
    <>
      <p ref={ref} className={`edm-host__bio ${open ? '' : 'is-folded'}`}>{bio}</p>
      {(overflows || open) && (
        <button type="button" className="edm-more edm-more--sm" onClick={() => setOpen(!open)}>
          {open ? 'Show less' : 'Read more'}
        </button>
      )}
    </>
  );
}

const sentLine = (update) => {
  const when = new Date(update.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return update.recipient_count != null
    ? `Sent ${when} to ${update.recipient_count} ${update.recipient_count === 1 ? 'person' : 'people'}`
    : `Sent ${when}`;
};

/**
 * What a member sees when they open an event: what it is, when and where, who is running
 * it, anything the host has said, and one decision, always reachable at the bottom of the
 * screen. Nothing operational lives here - the roster, check-in, the host list and sending
 * updates are all on the Manage screen behind the one button at the top.
 *
 * The featured game is the exception to that split. On a board game, video game or book club
 * night it is the thing people are deciding about, so it shows here for everyone and the host
 * edits it in place rather than from Manage. `featured` is null on any other category.
 */
function EventDetailMember({
  event,
  currentUser,
  canManage,
  myRsvp,
  onSetRsvp,
  onSaveNote,
  onRemoveRsvp,
  eventUpdates = [],
  backTo = '/events',
  backLabel = 'All events',
  manageTo,
  featured = null,
  canManageFeatured = false,
  onAssignFeatured,
  onRemoveFeatured
}) {
  const [descOpen, setDescOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const image = eventImage(event);
  const hosts = event.hosts || [];
  // Every host who wrote a bio gets a card, co-hosts included.
  const hostsWithBios = hosts.filter((host) => host.host_profile);
  const status = String(myRsvp?.status || '').trim().toLowerCase() || null;
  const note = myRsvp?.message || '';

  const description = event.description || '';
  const showDescToggle = description.length > 150;
  // A cancelled night keeps its page, so people who planned on it find out, but nobody can
  // say they are going to it. Taking an existing RSVP off is still allowed.
  const cancelled = isEventCancelled(event);

  return (
    <div className="edm">
      <div className="edm-topbar">
        <Link className="edm-back" to={backTo}>
          <DSIcon name="arrowLeft" size="sm" />
          {backLabel}
        </Link>
        {canManage && (
          <Link className="edm-manage" to={manageTo}>
            <DSIcon name="wrench" size="sm" />
            Manage
          </Link>
        )}
      </div>

      <EventCancelledBanner
        event={event}
        note={cancelled && canManage ? 'RSVPs are closed. Manage still works, and an admin can reinstate it from Series dates.' : null}
      />

      <article className={`edm-card${cancelled ? ' edm-card--cancelled' : ''}`}>
        <div className="edm-art">
          {image
            ? <img className="edm-art__img" src={image} alt="" />
            : <span className="edm-art__mono" aria-hidden="true">{monogram(event.title)}</span>}
          <span className="edm-art__scrim" aria-hidden="true" />
          {event.event_category && (
            <span className="edm-art__cat">
              {getCategoryLabel(event.event_category)}{event.is_private && ' · Private'}
            </span>
          )}
          <h1 className="edm-art__title">{event.title}</h1>
        </div>

        <div className="edm-card__body">
          <div className="edm-line">
            <span className="edm-line__icon"><DSIcon name="calendar" size="sm" /></span>
            <span className="edm-line__text">
              <span className="edm-line__strong">{longDate(event.event_date)}</span>
              <span className="edm-line__muted">{clockTime(event.event_date)}</span>
            </span>
          </div>

          {event.location && (
            <>
              <span className="edm-rule" />
              <div className="edm-line edm-line--top">
                <span className="edm-line__icon"><DSIcon name="pin" size="sm" /></span>
                <span className="edm-line__place">{event.location}</span>
              </div>
            </>
          )}

          {hosts.length > 0 && (
            <>
              <span className="edm-rule" />
              <div className="edm-line">
                <span className="edm-chips">
                  {hosts.map((host) => <UserAvatar key={host.id} person={host} className="edm-chip" />)}
                </span>
                <span className="edm-line__text">
                  <span className="edm-line__name">{hosts.map(fullName).join(', ')}</span>
                  <span className="edm-line__role">{hosts.length > 1 ? 'Hosts' : 'Host'}</span>
                </span>
              </div>
            </>
          )}

          {description && (
            <>
              <p className="edm-desc">
                {descOpen || !showDescToggle ? description : clamp(description, 150)}
              </p>
              {showDescToggle && (
                <button type="button" className="edm-more" onClick={() => setDescOpen(!descOpen)}>
                  {descOpen ? 'Show less' : 'Read more'}
                </button>
              )}
            </>
          )}
        </div>
      </article>

      {/* A cap is a fact worth knowing before deciding. A headcount is not: it tells the
          whole room how popular the night is, which is the host's business. */}
      {event.max_capacity ? (
        <div className="edm-capacity">
          <span className="edm-line__icon"><DSIcon name="users" size="sm" /></span>
          <span>Limited to {event.max_capacity} players for this night</span>
        </div>
      ) : null}

      {featured && (
        <section className="edm-panel">
          <div className="edm-panel__head">
            <h2 className="edm-panel__title">Featured {featured.label}</h2>
            {canManageFeatured && (
              <span className="edm-panel__actions">
                <button type="button" className="edm-more edm-more--sm" onClick={onAssignFeatured}>
                  {featured.name ? 'Change' : `Add a ${featured.label.toLowerCase()}`}
                </button>
                {featured.name && (
                  <button
                    type="button"
                    className="edm-more edm-more--sm edm-more--danger"
                    onClick={onRemoveFeatured}
                  >
                    Remove
                  </button>
                )}
              </span>
            )}
          </div>

          {featured.name ? (
            <>
              <p className="edm-featured">
                <span className="edm-line__icon"><DSIcon name={featured.icon} size="sm" /></span>
                <span className="edm-featured__name">{featured.name}</span>
              </p>
              {featured.description && <p className="edm-featured__desc">{featured.description}</p>}
            </>
          ) : (
            <p className="edm-panel__empty">
              {canManageFeatured
                ? `Nothing picked yet. Add the ${featured.label.toLowerCase()} you are running so people know what they are turning up for.`
                : `The host has not picked a ${featured.label.toLowerCase()} yet.`}
            </p>
          )}
        </section>
      )}

      {hostsWithBios.length > 0 && (
        <section className="edm-panel">
          <h2 className="edm-panel__title">{hostsWithBios.length > 1 ? 'Your hosts' : 'Your host'}</h2>
          <div className="edm-hosts">
            {hostsWithBios.map((host) => (
              <div className="edm-host" key={host.id}>
                <UserAvatar person={host} className="edm-chip edm-chip--lg" />
                <div className="edm-host__body">
                  <p className="edm-host__name">{fullName(host)}</p>
                  {host.host_since && <p className="edm-host__since">Host since {host.host_since}</p>}
                  <HostBio bio={host.host_profile} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="edm-panel">
        <h2 className="edm-panel__title">Updates from the host</h2>
        {eventUpdates.length > 0 ? (
          <div className="edm-updates">
            {eventUpdates.map((update) => (
              <div key={update.id} className="edm-update">
                <p className="edm-update__meta">{sentLine(update)}</p>
                <p className="edm-update__body">{update.message}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="edm-panel__empty">
            No updates yet. The host can message everyone who is going or interested.
          </p>
        )}
      </section>

      {/* Nothing to decide on a cancelled night. Someone who had RSVP'd can still take it off. */}
      {currentUser && cancelled && (
        <div className="edm-bar edm-bar--cancelled">
          <div className="edm-bar__inner">
            <div className="edm-bar__head">
              <span className="edm-bar__prompt">Cancelled, RSVPs are closed</span>
              {status && (
                <span className="edm-bar__links">
                  <button type="button" className="edm-bar__remove" onClick={onRemoveRsvp}>
                    Remove my RSVP
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* The decision stays on screen rather than being something to scroll back to. */}
      {currentUser && !cancelled && (
        <div className="edm-bar">
          <div className="edm-bar__inner">
            <div className="edm-bar__head">
              <span className="edm-bar__prompt">{PROMPT[status] || 'Are you going?'}</span>
              <span className="edm-bar__links">
                {status && !noteOpen && (
                  <button
                    type="button"
                    className="edm-bar__link"
                    onClick={() => { setNoteDraft(note); setNoteOpen(true); }}
                  >
                    {note ? 'Edit message' : 'Add a message'}
                  </button>
                )}
                {status && (
                  <button type="button" className="edm-bar__remove" onClick={onRemoveRsvp}>
                    Remove my RSVP
                  </button>
                )}
              </span>
            </div>

            <div className="edm-bar__choices" role="radiogroup" aria-label="Are you going?">
              {CHOICES.map((choice) => (
                <button
                  key={choice.value}
                  type="button"
                  role="radio"
                  aria-checked={status === choice.value}
                  className={`edm-pick${status === choice.value ? ` is-${choice.value}` : ''}`}
                  onClick={() => onSetRsvp(choice.value)}
                >
                  {choice.label}
                </button>
              ))}
            </div>

            {note && !noteOpen && <p className="edm-bar__note">{note}</p>}

            {noteOpen && (
              <div className="edm-bar__editor">
                <textarea
                  className="edm-bar__textarea"
                  rows="2"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Optional message for the host"
                />
                <div className="edm-bar__editorActions">
                  <button type="button" className="edm-bar__cancel" onClick={() => setNoteOpen(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="edm-bar__save"
                    onClick={() => { onSaveNote(noteDraft); setNoteOpen(false); }}
                  >
                    Save message
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetailMember;
